/**
 * @file jwt-decode.ts
 * @description Decode (no signature check) the payload of a JWT. We use
 *   this client-side to read `role` from the current access token so the
 *   SPA can show/hide admin-only UI. The server is the trust boundary —
 *   never trust a role read this way for security decisions.
 * @module Lib
 * @security This decoder NEVER verifies the signature. Any privileged
 *   action MUST be enforced by the API, which DOES verify. Client-side
 *   role is for UX (hide admin link from non-admins) only.
 */

export interface DecodedAccessToken {
  sub: string;
  role: 'admin' | 'staff' | 'customer';
  sid: string;
  email_verified: boolean;
  locale: 'he' | 'en';
  exp: number;
  iat: number;
  jti?: string;
}

export function decodeJwtPayload(token: string): DecodedAccessToken | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    const payload = JSON.parse(json) as DecodedAccessToken;
    if (
      typeof payload.sub !== 'string' ||
      (payload.role !== 'admin' && payload.role !== 'staff' && payload.role !== 'customer')
    ) {
      return null;
    }
    return payload;
  } catch {
    return null;
  }
}
